import moment from "moment";


// Custom ordering for type. Based on feedback from operations.
const TYPE_PRIORITY = [
  "sigmet" ,
  "airmet",
  "pirep",
  "metar",
  "taf",
  "upperwind",
  "notam",
  "image"
];


function compareByType(typeA, typeB) {
  const priorityA = TYPE_PRIORITY.findIndex(x => x === typeA);
  const priorityB = TYPE_PRIORITY.findIndex(x => x === typeB);
  if (priorityA === -1 && priorityB === -1) {
    return typeA > typeB ? 1 : typeA < typeB ? -1 : 0;
  }
  return priorityA > priorityB ? 1 : priorityA < priorityB ? -1 : 0;
}


/**
 * Normalize the data from the api to assure records of all types
 * can be treated uniformly
 *
 * Add a recordIdentifier to each row so that we can use that to group records
 */
export function normalizeData(record) {
  record.bulletinIdentifier = `${record.type}:${record.pk}`;
  record.recordIdentifier = record.bulletinIdentifier;

  // The upper wind bulletins should group based on site not based on bulletinId
  if (record.type === "upperwind") {
    record.recordIdentifier = `${record.bulletinIdentifier}:${record.location}`;
  }

  // Set the filtration column
  if (record.type === "image") {
    record.filterText = record.translated.text;
  } else if (record.type === "notam") {
    record.filterText = record.formattedBulletinWithQLine;
  } else {
    record.filterText = record.formattedBulletin;
  }
  return record;
}


export function translateData(record, meta, intl) {
  if (record.type === "image") {
    const compositeKey = record.location
      .split("/")
      .filter(v => v)
      .map(x => intl.formatMessage({ id: x }));
    record.translated = {
      type: compositeKey[0],
      location: getLocation(
        compositeKey.slice(1).join(" "),
        record,
        meta,
        intl
      ),
      text: compositeKey.join(" ")
    };
  } else {
    var bulletinType = record.type.toUpperCase()
    if (record.text.includes('SAWR')){
      bulletinType = 'SAWR'
    }
    record.translated = {
      type: intl.formatMessage({ id: bulletinType }),
      location: getLocation(record.location || "", record, meta, intl),
      text: record.text
    };
  }
  return record;
}


export function findPoint(pointReference, tags) {
  // Given a point reference index, which is the index of the leg + the fraction of a leg from 0 to 1,
  // find the point that either exactly matches or is closest to the given point.
  let pointIndex = Math.floor(pointReference);
  if (tags.length > pointIndex) {
    return {
      text: tags[pointIndex].name,
      exactMatch: pointReference === pointIndex
    };
  } else {
    return {};
  }
}


function isFIR(pointReference, tags) {
  // Given a point reference, return true if it refers to an FIR.
  // let tags = this.props.searchData.searchParameters.tags;
  for (let { ident, type } of tags) {
    if (ident === pointReference) {
      return type === "fir";
    }
  }
  return false;
}


function getLocationDescriptionNearPoint(pointReference, intl) {
  // Returns a string representing a "near" point
  return intl.formatMessage(
    { id: "near_location" },
    { pointReference: pointReference }
  );
}


export function getLocationDescription(
  pointReference,
  radialDistance,
  meta,
  intl
) {
  // Return a dictionary containing:
  // * label: the nearest point. We use the when deciding whether to display the location, the
  //          location description, or both
  // * description: the formatted string. Something like "In CZUL," "Near CYOW," or "En route"
  let locationDescription = pointReference;

  let fir = isFIR(pointReference, meta.tags);
  if (fir) {
    // Check first if the reference point is within the FIR.
    locationDescription = intl.formatMessage(
      { id: "in_fir" },
      { pointReference: pointReference }
    );
  } else if (meta.isRoute) {
    // Figure out which point in the route this point is closest to
    let point = findPoint(pointReference, meta.tags);
    // Is it an exact match?
    if (point.exactMatch) {
      locationDescription =
        radialDistance === 0
          ? point.text
          : getLocationDescriptionNearPoint(point.text, intl);
    } else {
      const destinationIndex = Math.ceil(pointReference);
      locationDescription = intl.formatMessage(
        { id: "en_route_to" },
        {ident:meta.tags[destinationIndex]['name']}
      );
    }
  } else if (radialDistance !== 0) {
    locationDescription = getLocationDescriptionNearPoint(pointReference, intl);
  }

  return locationDescription;
}


export function columnFilter(filterValue, column, invertTestResult, isRegex) {
  // return a function that will search for filterValue:
  if (filterValue === "") {
    return () => {
      return true;
    };
  }
  const regex = isRegex
    ? filterValue
    : filterValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const filterExp = new RegExp(regex, "i");
  return function (data) {
    let testResult = filterExp.test(String(data[column]));
    return invertTestResult ? !testResult : testResult;
  };
}


export function getLocation(label, record, meta, intl) {
  let location = {
    label: label,
    description: "",
  };

  // If we don't have an explicit location but the record is directly intersecting with our search
  // polygon, we may still be able to get a location
  if (!location.label && record.radialDistance === 0) {
    // If it's a route we handle it specially
    if (meta.isRoute) {
      // Find and use an exactly-matching point, if there is one
      let point = findPoint(record.pointReference, meta.tags);
      if (point.exactMatch) {
        location.label = point.text;
      }
    } else {
      // Some polygons (specifically NOTAMs) don't have a location field set, so even when we're
      // just querying a single point, they wouldn't have a location. However, because the radial
      // distance is 0 it means that they are right overtop the given "pointReference", so just
      // use that
      location.label = record.pointReference;
    }
  }

  // Only get the location description if we queried for more than one tag
  if (meta.tags.length > 1) {
    // Get the location description, which is a dictionary containing the ident (e.g., CYOW), and
    // the text that we'd use to return to the user
    location.description = getLocationDescription(
      record.pointReference,
      record.radialDistance,
      meta,
      intl
    );
    // If the location and the description are the same, only display the location
    if (location.label === location.description) {
      location.description = "";
    }
  }

  return location;
}


export function sortData(searchData, sorting) {
  if (!(searchData && searchData.data)) {
    return [];
  }
  const searchTags = searchData.searchParameters.tags;

  const containsFir = searchData.searchParameters.tags.some((tag) => window.FIRS.some((fir) => fir === tag.name))

  searchData.data.sort((r1, r2) => {
    // First we sort by bulletin type given the order described in TYPE_SORT_ORDER
    const typeIndex = compareByType(r1.type, r2.type)
    if (typeIndex !== 0) return typeIndex;

    // Second we sort by tag position so results appear in the order requested
    let r1LocationIndex = searchTags.findIndex(x => x.name === r1.location);
    let r2LocationIndex = searchTags.findIndex(x => x.name === r2.location);

    if (!containsFir){
      if(searchData.searchParameters.radius.on){
        if(searchTags.length > 1){
          if(r1.pointReference < r2.pointReference) return -1;
          if(r1.pointReference > r2.pointReference) return 1;
        }
      }else{
        // If the row's location tag is not found in the tag list, use the point reference
        // We consider the point reference a lower priority than the location field as the location field
        // represents the bulletin's actual location, whereas the point reference is merely which tag
        // is closest
        if (r1LocationIndex === -1){
          r1LocationIndex = Math.abs(searchTags.findIndex(x => x.name === r1.pointReference)) + 0.5;
        }
        if (r2LocationIndex === -1){
          r2LocationIndex = Math.abs(searchTags.findIndex(x => x.name === r2.pointReference)) + 0.5;
        }

        if (r1LocationIndex < r2LocationIndex) return -1;
        if (r1LocationIndex > r2LocationIndex) return 1;
      }

      // Use the radial distance if locations all match up; this means bulletins farther away will be sorted further down the list
      if(r1.radialDistance < r2.radialDistance) return -1;
      if(r1.radialDistance > r2.radialDistance) return 1;
    }
    // Third, we sort by time (start validity specifically)
    const timeDiff = moment.utc(r1.startValidity).diff(moment.utc(r2.startValidity))
    if (timeDiff > 0) return -1;
    if (timeDiff < 0) return 1;

    // Fouth and finally, we sort by unique id string
    return r1.pk < r2.pk ? -1 : 1
  });
  return searchData.data;
}


/**
 * Filter search results based on a set of configurable filters
 *
 * @param {Object[]} sortedData - Arbitrary objects to filter
 * @param {Object[]} filterConfig - Filtering configurations
 * @param {string} filterConfig[].filter - The text to filter on (returns true if filter is a sub string of sortedData[column]).
 * @param {string} filterConfig[].column - The attribute of the data to apply filter to
 * @param {string} filterConfig[].invertTestResult - [Optional] Inverts the result of filter
 * @param {string} filterConfig[].preProcessor - [Optional] Arbitrary function to run on sortedData prior to filtering
 * @returns {[Object[], number]}
 */
export function filterData(
  sortedData,
  duplicateFilterConfig,
  textFilterConfig
) {
  let resultCount = 0;

  const textFilters = textFilterConfig.map(c => [
    columnFilter(c.filter, c.column, c.invertTestResult, c.isRegex),
    c.preProcessor
  ]);
  const duplicateFilter = {};

  sortedData.map(d => {
    // Start as visible
    d.hidden = false;

    // Add at least self to locations
    const uniqueLocation = {
      location: d.translated.location,
      pointReference: d.pointReference,
      radialDistance: d.radialDistance
    };
    d.locations = [uniqueLocation];

    // Check duplicates (based on recordIdentifier NOT bulletinIdentifier)
    if (duplicateFilterConfig) {
      if (!duplicateFilter.hasOwnProperty(d.recordIdentifier)) {
        // Only or First time we have encountered this record
        duplicateFilter[d.recordIdentifier] = d;
      } else {
        // Already encountered this record
        duplicateFilter[d.recordIdentifier].locations.push(uniqueLocation);
        d.hidden = true;
      }
    }

    // Check Text
    if (d.hidden === false) {
      const filterHides = f => {
        const [filterMatches, preProcessor] = f;
        const object = preProcessor ? preProcessor(d) : d;
        return !filterMatches(object);
      };

      if (textFilters.some(filterHides)) {
        d.hidden = true;
      }
    }

    if (!d.hidden) resultCount += 1;
    return d;
  });
  return [sortedData, resultCount];
}


export function truthyOrZero(value) {
  return Boolean(Boolean(value) === true || value === 0);
}


/**
 * Test a leg pointReference to see if it should be inserted into the results
 *
 * @param {{pointReference: number, radialDistance: number}} position Position on current leg
 * @param {{pointReference: number, radialDistance: number}} previousPosition Position on previous leg
 * @returns {boolean} Should this result in a duplicate bulletin at the position for the current leg
 */
export function isValidPosition(position, previousPosition) {
  // Does not intersect on this leg
  if (!truthyOrZero(position)) {
    return false;
  }

  // At/before Origin but previous did intersect
  if (position.pointReference === 0 && truthyOrZero(previousPosition)) {
    return false;
  }

  // En Route but was at/after the destination of the previous leg
  if (previousPosition && previousPosition.pointReference === 1) {
    return false;
  }

  // There is an additional case that corresponds to the inside of the turn that
  // is within the radius of the waypoint but we aren't bothering with it at this time

  return true;
}


/**
 * Generate a list of records for each value of pointReference
 *
 * @param record original record
 * @returns {any[]}
 */
export function expandPosition(record) {
  if (record.position instanceof Array) {
    if (record.type === 'metar'){
      // Special case for METAR, as they are grouped together and thus don't behave the same as other bulletins
      let closestPosition = null;
      let closestIndex = null;
      record.position.forEach((position, index) => {
        if(closestPosition === null){
          closestPosition = position;
          closestIndex = index;
        } else if (isValidPosition(position, closestPosition)) {
          if(position.pointReference < closestPosition.pointReference) {
            closestPosition = position;
            closestIndex = index;
          }
        }
      })
      record.pointReference = closestIndex + closestPosition.pointReference;
      record.radialDistance = closestPosition.radialDistance;
      return [record]
    }else{
      const newRecords = [];

      for (let i = 0; i < record.position.length; i++) {
        const position = record.position[i];
        const previousPosition = i === 0 ? null : record.position[i - 1];
        if (isValidPosition(position, previousPosition)) {
          let newRecord = Object.assign({}, record);
          // Add the leg index so that points sort correctly
          newRecord.pointReference = i + position.pointReference;
          newRecord.radialDistance = position.radialDistance;
          newRecords.push(newRecord);
        }
      }

      // Make sure we generate at least one record in case of error
      if (newRecords.length === 0) {
        record.pointReference = 0;
        record.radialDistance = 0;
        return [record];
      } else {
        return newRecords;
      }
    }
  } else {
    record.pointReference = record.position.pointReference;
    record.radialDistance = record.position.radialDistance;
    return [record];
  }
}
